import React, { useId, useMemo, useState } from 'react'
import { HexShape } from './HexShape'
import {
  ARROW_SRC,
  CELL_H,
  EASE_BOUNCE,
  EXPANDED_BG_ALPHA,
  EXPANDED_BG_WIDTH,
  hexHeightForWidth,
  INNER_HEX_WIDTH,
  tierTone,
  type HexTone,
} from './hexTheme'
import type { ExpansionPhase } from '@/hooks/useHexExpansion'
import type { ExpandSummary } from './expandSummary'
import { layersFromTick, type MetricLayer } from './layers'
import {
  ARROW_DURATION_MS,
  ARROW_START_MS,
  BG_DURATION_MS,
  BG_STAGGER_MS,
  EXPAND_EASE,
  EXPAND_EASE_PULSE,
  INNER_DURATION_MS,
  INNER_OVERSHOOT,
  INNER_STAGGER_MS,
  INNER_START_MS,
  TEXT_DURATION_MS,
  TEXT_START_MS,
} from './expandAnim'

interface Props {
  summary: ExpandSummary
  /** Live tick — metrics update while grid order is frozen. */
  tick: unknown
  phase: ExpansionPhase
  /** Layout-computed tile width (~3 cell slots). */
  bandWidth: number
  onDrill?: () => void
  canDrill?: boolean
}

/**
 * Slot index ← rank in worst→best sorted layers.
 * Center = worst (rank 0); far left/right = best (ranks 3/4).
 */
const CENTER_OUT_RANKS = [3, 1, 0, 2, 4] as const

const ARROW_SIZE = 40
const IDENTITY_GAP = 18

function placeCenterOut(sorted: MetricLayer[]): MetricLayer[] {
  const out: MetricLayer[] = new Array(5)
  for (let slot = 0; slot < 5; slot++) {
    const rank = CENTER_OUT_RANKS[slot]!
    out[slot] = sorted[rank] ?? sorted[sorted.length - 1]!
  }
  return out
}

export function HexExpandedRow({ summary, tick, phase, bandWidth, onDrill, canDrill }: Props) {
  const uid = useId().replace(/:/g, '')
  const [arrowHover, setArrowHover] = useState(false)
  const bgW = EXPANDED_BG_WIDTH
  const bgH = hexHeightForWidth(bgW)
  const innerH = hexHeightForWidth(INNER_HEX_WIDTH)
  const bandW = Math.max(bandWidth, bgW * 2)
  const bandH = Math.max(CELL_H, bgH) + IDENTITY_GAP

  const slots = useMemo(() => {
    const sorted = layersFromTick(tick)
    return placeCenterOut(sorted)
  }, [tick])

  // Background hexes fill the expanded band with intentional overlap.
  const bgCenters = Array.from({ length: 5 }, (_, i) => {
    const min = bgW / 2
    const max = bandW - bgW / 2
    return min + (i * (max - min)) / 4
  })

  const detailCenters = bgCenters
  const hexRowTop = bandH / 2 - IDENTITY_GAP / 2

  const studioLine =
    summary.studioCode && summary.studioCode !== '—'
      ? summary.studioName && summary.studioName !== summary.studioCode
        ? `${summary.studioName} (${summary.studioCode})`
        : summary.studioCode
      : ''

  const animActive = phase !== 'push' && phase !== 'closing' && phase !== 'reflow'
  const settled = phase === 'open' || phase === 'closing'

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: bandH,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <style>{`
        @keyframes hexBgIn-${uid} {
          from { opacity: 0; transform: translate(-50%, -50%) scale(0.5); }
          to   { opacity: ${EXPANDED_BG_ALPHA}; transform: translate(-50%, -50%) scale(1); }
        }
        @keyframes hexInnerPulse-${uid} {
          0%   { opacity: 0; transform: scale(0.5); }
          72%  { opacity: 1; transform: scale(${INNER_OVERSHOOT}); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes hexFadeIn-${uid} {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>

      <div
        style={{
          position: 'relative',
          width: bandW,
          height: bandH,
        }}
      >
        {bgCenters.map((cx, i) => {
          const layer = slots[i]!
          const tone = tierTone(layer.tier)
          const delay = i * BG_STAGGER_MS
          const style: React.CSSProperties = settled
            ? {
                position: 'absolute',
                left: cx,
                top: hexRowTop,
                width: bgW,
                height: bgH,
                transform: 'translate(-50%, -50%) scale(1)',
                opacity: EXPANDED_BG_ALPHA,
                zIndex: i + 1,
                pointerEvents: 'none',
              }
            : {
                position: 'absolute',
                left: cx,
                top: hexRowTop,
                width: bgW,
                height: bgH,
                zIndex: i + 1,
                pointerEvents: 'none',
                opacity: 0,
                transform: 'translate(-50%, -50%) scale(0.5)',
                animation: animActive
                  ? `hexBgIn-${uid} ${BG_DURATION_MS}ms ${EXPAND_EASE} ${delay}ms both`
                  : undefined,
              }
          return (
            <div key={`bg-${i}`} style={style}>
              <HexShape tone={tone} width={bgW} gradKey={`exp-bg-${i}`} opacity={1} gradient="expanded" />
            </div>
          )
        })}

        {detailCenters.map((cx, i) => {
          const layer = slots[i]!
          const tone: HexTone = tierTone(layer.tier)
          const delay = INNER_START_MS + i * INNER_STAGGER_MS
          const textDelay = TEXT_START_MS + i * 18
          const title = layer.label
          const value = layer.valueText ?? '—'

          const wrapStyle: React.CSSProperties = settled
            ? {
                position: 'absolute',
                left: cx,
                top: hexRowTop,
                width: INNER_HEX_WIDTH,
                height: innerH,
                marginLeft: -INNER_HEX_WIDTH / 2,
                marginTop: -innerH / 2,
                transform: 'scale(1)',
                opacity: 1,
                zIndex: 20 + i,
                pointerEvents: 'none',
              }
            : {
                position: 'absolute',
                left: cx,
                top: hexRowTop,
                width: INNER_HEX_WIDTH,
                height: innerH,
                marginLeft: -INNER_HEX_WIDTH / 2,
                marginTop: -innerH / 2,
                zIndex: 20 + i,
                pointerEvents: 'none',
                opacity: 0,
                transform: 'scale(0.5)',
                animation: animActive
                  ? `hexInnerPulse-${uid} ${INNER_DURATION_MS}ms ${EXPAND_EASE_PULSE} ${delay}ms both`
                  : undefined,
              }

          return (
            <div key={`inner-${i}`} style={wrapStyle}>
              <HexShape
                tone={tone}
                width={INNER_HEX_WIDTH}
                gradKey={`exp-in-${i}`}
                opacity={1}
                solid
              />
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  padding: '0 6px',
                  opacity: settled ? 1 : 0,
                  animation: !settled && animActive
                    ? `hexFadeIn-${uid} ${TEXT_DURATION_MS}ms ${EXPAND_EASE} ${textDelay}ms both`
                    : undefined,
                }}
              >
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    color: 'rgba(238,240,248,0.95)',
                    lineHeight: 1.15,
                    maxWidth: INNER_HEX_WIDTH - 6,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={title}
                >
                  {title}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    marginTop: 3,
                    fontWeight: 500,
                    color: 'rgba(238,240,248,0.9)',
                    fontFamily: 'var(--font-mono, monospace)',
                  }}
                  title={value}
                >
                  {value}
                </div>
              </div>
            </div>
          )
        })}

        {/* Identity under the metric hexes */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: hexRowTop + innerH / 2 + 10,
            transform: 'translateX(-50%)',
            zIndex: 30,
            textAlign: 'center',
            pointerEvents: 'none',
            opacity: settled ? 1 : 0,
            animation: !settled && animActive
              ? `hexFadeIn-${uid} ${TEXT_DURATION_MS}ms ${EXPAND_EASE} ${TEXT_START_MS + 80}ms both`
              : undefined,
            maxWidth: bandW - 48,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: 'rgba(238,240,248,0.95)',
              fontFamily: 'var(--font-mono, monospace)',
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={summary.label}
          >
            {summary.label}
          </div>
          {studioLine ? (
            <div
              style={{
                fontSize: 10,
                marginTop: 2,
                color: 'rgba(238,240,248,0.72)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={studioLine}
            >
              {studioLine}
            </div>
          ) : null}
        </div>

        {bgCenters[4] != null && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              if (canDrill) onDrill?.()
            }}
            onMouseEnter={() => setArrowHover(true)}
            onMouseLeave={() => setArrowHover(false)}
            disabled={!canDrill}
            aria-label="Deep dive"
            style={{
              position: 'absolute',
              left: (bgCenters[4]! + bgW / 2 - ARROW_SIZE * 0.6) - 6,
              top: hexRowTop,
              transform: 'translateY(-50%)',
              width: ARROW_SIZE,
              height: ARROW_SIZE,
              padding: 0,
              border: 'none',
              borderRadius: 0,
              background: 'transparent',
              cursor: canDrill ? 'pointer' : 'default',
              zIndex: 40,
              opacity: settled ? (canDrill ? 1 : 0.45) : 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
              animation:
                !settled && animActive
                  ? `hexFadeIn-${uid} ${ARROW_DURATION_MS}ms ${EXPAND_EASE} ${ARROW_START_MS}ms both`
                  : undefined,
            }}
          >
            <img
              src={ARROW_SRC}
              alt=""
              width={ARROW_SIZE - 10}
              height={ARROW_SIZE - 10}
              draggable={false}
              style={{
                display: 'block',
                transform: arrowHover && canDrill
                  ? 'translateX(3px) scale(1.12)'
                  : 'none',
                transition: `transform 180ms ${EASE_BOUNCE}`,
              }}
            />
          </button>
        )}
      </div>
    </div>
  )
}
